import React, { useEffect, useMemo, useState, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';
// ============================================================================
// StateAutocomplete
// ----------------------------------------------------------------------------
// Lightweight searchable dropdown for US States and Canadian Provinces.
// - Type to filter, click or Enter to select
// - Arrow keys + Escape supported
// - Free-text values are still allowed (international users) — typed text
//   that doesn't match a known state is preserved on blur
// - Visual style matches the surrounding form inputs (My Info page)
// ============================================================================
export interface StateOption {
  name: string;
  code: string;
  country: 'US' | 'CA';
}
const US_STATES: StateOption[] = [['Alabama', 'AL'], ['Alaska', 'AK'], ['Arizona', 'AZ'], ['Arkansas', 'AR'], ['California', 'CA'], ['Colorado', 'CO'], ['Connecticut', 'CT'], ['Delaware', 'DE'], ['District of Columbia', 'DC'], ['Florida', 'FL'], ['Georgia', 'GA'], ['Hawaii', 'HI'], ['Idaho', 'ID'], ['Illinois', 'IL'], ['Indiana', 'IN'], ['Iowa', 'IA'], ['Kansas', 'KS'], ['Kentucky', 'KY'], ['Louisiana', 'LA'], ['Maine', 'ME'], ['Maryland', 'MD'], ['Massachusetts', 'MA'], ['Michigan', 'MI'], ['Minnesota', 'MN'], ['Mississippi', 'MS'], ['Missouri', 'MO'], ['Montana', 'MT'], ['Nebraska', 'NE'], ['Nevada', 'NV'], ['New Hampshire', 'NH'], ['New Jersey', 'NJ'], ['New Mexico', 'NM'], ['New York', 'NY'], ['North Carolina', 'NC'], ['North Dakota', 'ND'], ['Ohio', 'OH'], ['Oklahoma', 'OK'], ['Oregon', 'OR'], ['Pennsylvania', 'PA'], ['Rhode Island', 'RI'], ['South Carolina', 'SC'], ['South Dakota', 'SD'], ['Tennessee', 'TN'], ['Texas', 'TX'], ['Utah', 'UT'], ['Vermont', 'VT'], ['Virginia', 'VA'], ['Washington', 'WA'], ['West Virginia', 'WV'], ['Wisconsin', 'WI'], ['Wyoming', 'WY']].map(([name, code]) => ({
  name,
  code,
  country: 'US' as const
}));
const CA_PROVINCES: StateOption[] = [['Alberta', 'AB'], ['British Columbia', 'BC'], ['Manitoba', 'MB'], ['New Brunswick', 'NB'], ['Newfoundland and Labrador', 'NL'], ['Northwest Territories', 'NT'], ['Nova Scotia', 'NS'], ['Nunavut', 'NU'], ['Ontario', 'ON'], ['Prince Edward Island', 'PE'], ['Quebec', 'QC'], ['Saskatchewan', 'SK'], ['Yukon', 'YT']].map(([name, code]) => ({
  name,
  code,
  country: 'CA' as const
}));
const ALL_OPTIONS: StateOption[] = [...US_STATES, ...CA_PROVINCES];
interface StateAutocompleteProps {
  value: string;
  onChange: (value: string, option?: StateOption) => void;
  disabled?: boolean;
  darkMode?: boolean;
  placeholder?: string;
  className?: string;
}
export const StateAutocomplete: React.FC<StateAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  darkMode = false,
  placeholder = 'Select state / province',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Keep internal query in sync with parent value (when not focused/editing)
  useEffect(() => {
    if (!isOpen) setQuery(value);
  }, [value, isOpen]);
  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_OPTIONS;
    return ALL_OPTIONS.filter(opt => opt.name.toLowerCase().includes(q) || opt.code.toLowerCase().startsWith(q));
  }, [query]);
  // Reset highlight when filter changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, isOpen]);
  // Scroll highlighted option into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(`[data-index="${highlightedIndex}"]`);
    if (el) el.scrollIntoView({
      block: 'nearest'
    });
  }, [highlightedIndex, isOpen]);
  const selectOption = (opt: StateOption) => {
    onChange(opt.name, opt);
    setQuery(opt.name);
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && filteredOptions[highlightedIndex]) {
        e.preventDefault();
        selectOption(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery(value);
    } else if (e.key === 'Tab') {
      setIsOpen(false);
    }
  };
  const handleBlur = () => {
    // Preserve the user's typed text (free-text fallback for international
    // users), but only commit if it differs from current value.
    if (query !== value) onChange(query);
  };
  const inputClass = `w-full px-4 py-2 pr-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''} ${className}`;
  return <div ref={containerRef} className="relative" data-id="element-11423">
      <input ref={inputRef} type="text" value={query} onChange={e => {
      setQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    }} onFocus={() => !disabled && setIsOpen(true)} onBlur={handleBlur} onKeyDown={handleKeyDown} disabled={disabled} placeholder={placeholder} autoComplete="off" role="combobox" aria-expanded={isOpen} aria-autocomplete="list" className={inputClass} data-id="element-11424" />
      <ChevronDownIcon className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none transition-transform ${isOpen ? 'rotate-180' : ''} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11425" />

      {isOpen && !disabled && <ul ref={listRef} role="listbox" className={`absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border shadow-lg ${darkMode ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-11426">
          {filteredOptions.length === 0 ? <li className={`px-4 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11427">
              No matches — your entry will be saved as typed
            </li> : filteredOptions.map((opt, idx) => {
        const isHighlighted = idx === highlightedIndex;
        const isSelected = opt.name === value;
        return <li key={`${opt.country}-${opt.code}`} data-index={idx} role="option" aria-selected={isSelected}
        // Use onMouseDown so selection fires before input blur
        onMouseDown={e => {
          e.preventDefault();
          selectOption(opt);
        }} onMouseEnter={() => setHighlightedIndex(idx)} className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between ${isHighlighted ? darkMode ? 'bg-navy-700 text-white' : 'bg-blue-50 text-gray-900' : darkMode ? 'text-gray-200' : 'text-gray-900'} ${isSelected ? 'font-medium' : ''}`} data-id="element-11428">
                  <span data-id="element-11429">{opt.name}</span>
                  <span className={`text-xs ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11430">
                    {opt.code} · {opt.country}
                  </span>
                </li>;
      })}
        </ul>}
    </div>;
};